import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import * as yup from 'yup';

import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import { Alert, type AlertStateOrEmpty } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { AlreadyLoggedInNotice } from '../already-logged-in-notice';
import type { DefaultTrialProps } from '../trial-layout-switcher';

// The page itself (sections, globe, simulations, their CSS modules) is a separate chunk: every LP in
// the registry is imported statically by /trial, and this one ships ~200 KB of CSS/JS that no other
// LP needs. SSR stays on, so the markup is in the first HTML and the chunk is preloaded for hydration.
const TourBoostPage = dynamic(() => import('./tour-boost/page').then((module) => module.TourBoostPage));

const NAMESPACE = 'components/trial/tour-boost';

interface FormData {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

type FieldName = Exclude<keyof FormData, 'recaptcha'>;

// The five fields of the design's form, in order. The summary above the submit counts only these:
// the captcha carries its own message under the widget.
const FIELD_NAMES: FieldName[] = ['name', 'email', 'repeatEmail', 'password', 'passwordRepeat'];

const PASSWORD_MIN = 6;

const FIELD_CLASS = 'np-field grid min-w-0 grid-rows-[auto_auto_auto] content-start gap-1';
const FIELD_IN_ROW_CLASS = `${FIELD_CLASS} [@container_(width>=25rem)]:row-span-3 [@container_(width>=25rem)]:grid-rows-subgrid`;
const ROW_CLASS = 'np-form__row np-form__row--2 grid min-w-0 !gap-4 [@container_(width>=25rem)]:!grid-cols-2 [@container_(width>=25rem)]:grid-rows-[auto_auto_auto] [@container_(width>=25rem)]:!gap-x-5 [@container_(width>=25rem)]:!gap-y-1';
const LABEL_CLASS = 'np-field__label inline-flex min-w-0 items-center gap-[.4rem] justify-self-start text-[length:.75rem] font-semibold uppercase leading-[1.2] tracking-[.08em] text-[#CBD5E1] transition-colors duration-[160ms] ease-[var(--ease-out)] [overflow-wrap:anywhere]';

function OkIcon() {
  return (
    <svg className='np-field__ok' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <circle cx={12} cy={12} r={9} />
      <path d='m8.4 12.3 2.4 2.4 4.8-5' />
    </svg>
  );
}

function EyeIcons() {
  return (
    <>
      <svg className='ico-on' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
        <path d='M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z' />
        <circle cx={12} cy={12} r={3} />
      </svg>
      <svg className='ico-off' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
        <path d='M3 3l18 18' />
        <path d='M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16.6 16.6 0 0 1-3.2 3.7' />
        <path d='M6.6 6.7A16.4 16.4 0 0 0 2.5 12S6 18 12 18a9.6 9.6 0 0 0 4.2-.9' />
        <path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
      </svg>
    </>
  );
}

interface TrialFormProps {
  methods: UseFormReturn<FormData>;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  isDisabledButton: boolean;
  trialConfigId: number | null;
  trialPageId: number | null;
  popUp: { show: boolean; message: string; state: AlertStateOrEmpty };
  onClosePopUp: () => void;
  isSsoAuthenticating: boolean;
  onSsoStart: () => void;
  onSsoSuccess: () => void;
  onSsoError: (message: string) => void;
  onRecaptchaChange: (token: string | null) => void;
  onRecaptchaExpired: () => void;
  onRecaptchaError: () => void;
}

// The design's registration card (`form.np-form`, the "approved snippet" of the site shell) bound to
// react-hook-form. It subscribes to the form state itself, so typing re-renders the card and nothing
// else: the page around it is heavy and imperative. `useForm` lives one level up, in TourBoost, so
// the values survive the card moving between the hero column and the trial dialog.
function TrialForm({ methods, onSubmit, isLoading, isDisabledButton, trialConfigId, trialPageId, popUp, onClosePopUp, isSsoAuthenticating, onSsoStart, onSsoSuccess, onSsoError, onRecaptchaChange, onRecaptchaExpired, onRecaptchaError }: TrialFormProps) {
  const { t } = useTranslation(NAMESPACE);
  const { register, handleSubmit, control } = methods;
  const { errors, touchedFields, submitCount } = useFormState({ control });
  const values = useWatch({ control });
  const [visiblePasswords, setVisiblePasswords] = useState<Record<'password' | 'passwordRepeat', boolean>>({ password: false, passwordRepeat: false });

  const invalidCount = FIELD_NAMES.filter((fieldName) => errors[fieldName]).length;
  // The summary appears on a failed submit and re-counts as fields are fixed: 5 → 4 → … → gone.
  const summary = submitCount > 0 && invalidCount > 0 ? t('form.fix_count', { count: invalidCount }) : '';

  const fieldState = (fieldName: FieldName) => {
    const error = errors[fieldName]?.message;
    const isValid = !error && Boolean(touchedFields[fieldName] || submitCount > 0) && Boolean(values[fieldName]?.trim());
    let className = '';
    if (error) className = 'np-field--invalid';
    else if (isValid) className = 'np-field--valid';
    return { error, className };
  };

  const renderMessage = (fieldName: FieldName, id: string) => {
    const { error } = fieldState(fieldName);
    return (
      <span className={`np-field__msg ${error ? 'is-error' : ''}`} id={id}>
        {error}
      </span>
    );
  };

  const toggleVisibility = (fieldName: 'password' | 'passwordRepeat') => setVisiblePasswords((previous) => ({ ...previous, [fieldName]: !previous[fieldName] }));

  const name = fieldState('name');
  const email = fieldState('email');
  const repeatEmail = fieldState('repeatEmail');
  const password = fieldState('password');
  const passwordRepeat = fieldState('passwordRepeat');

  return (
    <form className='np-form relative isolate mx-auto w-full max-w-full !scroll-mt-8 rounded-3xl !border !border-white/10 p-6 text-[#F4F6F7] [container-type:inline-size]' id='np-form' method='post' data-trial-key='tour_boost' noValidate autoComplete='on' aria-labelledby='np-form-title' onSubmit={handleSubmit(onSubmit)}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      <div className='np-form__head mb-5 text-center'>
        <h2 className='np-form__title text-balance text-[clamp(1.375rem,1.1rem+1.1vw,1.75rem)] font-bold uppercase leading-[1.1] tracking-[-.01em] text-[#FFFFFF]' id='np-form-title'>
          <Trans t={t} i18nKey='form.title' components={{ span: <span className='np-form__hl text-[#93FF18] [text-shadow:0_0_10px_rgb(147_255_24/.32)]' /> }} />
        </h2>
        <p className='np-form__sub mt-2 text-[length:.875rem] leading-[1.4] text-[#CBD5E1]'>
          {t('common.no_card')}{' '}
          <span className='dot mx-[.2em] align-[-.1em] text-[1.35em] font-bold leading-[0] text-[#93FF18]' aria-hidden='true'>
            ·
          </span>{' '}
          {t('form.full_access')}
        </p>
      </div>

      <div className='mb-5'>
        <SsoProviders onAuthStart={onSsoStart} onAuthSuccess={onSsoSuccess} onAuthError={onSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
      </div>

      {popUp.show && (
        <div className='mb-5'>
          <Alert message={popUp.message} state={popUp.state} onClose={onClosePopUp} />
        </div>
      )}

      <p className='np-form__live !sr-only' role='status' aria-live='polite'>
        {summary}
      </p>

      <div className='np-form__fields grid min-w-0 gap-4 [container-type:inline-size]'>
        <div className={`${FIELD_CLASS} ${name.className}`}>
          <label className={LABEL_CLASS} htmlFor='np-name'>
            {t('form.label.name')}
            <OkIcon />
          </label>
          <div className='np-field__control relative min-w-0'>
            <input {...register('name')} dir='auto' className='np-field__input' id='np-name' type='text' maxLength={255} autoComplete='name' placeholder={t('form.placeholder.name')} required aria-required='true' aria-invalid={name.error ? 'true' : undefined} aria-describedby='np-name-msg' />
          </div>
          {renderMessage('name', 'np-name-msg')}
        </div>

        <div className={ROW_CLASS}>
          <div className={`${FIELD_IN_ROW_CLASS} ${email.className}`}>
            <label className={LABEL_CLASS} htmlFor='np-email'>
              {t('form.label.email')}
              <OkIcon />
            </label>
            <div className='np-field__control relative min-w-0'>
              <input {...register('email')} className='np-field__input' id='np-email' type='text' inputMode='email' maxLength={255} autoComplete='email' autoCapitalize='off' spellCheck='false' placeholder={t('form.placeholder.email')} required aria-required='true' aria-invalid={email.error ? 'true' : undefined} aria-describedby='np-email-msg' />
            </div>
            {renderMessage('email', 'np-email-msg')}
          </div>

          <div className={`${FIELD_IN_ROW_CLASS} ${repeatEmail.className}`}>
            <label className={LABEL_CLASS} htmlFor='np-email2'>
              {t('form.label.confirm_email')}
              <OkIcon />
            </label>
            <div className='np-field__control relative min-w-0'>
              <input {...register('repeatEmail')} className='np-field__input' id='np-email2' type='text' inputMode='email' maxLength={255} autoComplete='email' autoCapitalize='off' spellCheck='false' placeholder={t('form.placeholder.confirm_email')} required aria-required='true' aria-invalid={repeatEmail.error ? 'true' : undefined} aria-describedby='np-email2-msg' />
            </div>
            {renderMessage('repeatEmail', 'np-email2-msg')}
          </div>
        </div>

        <div className={ROW_CLASS}>
          <div className={`${FIELD_IN_ROW_CLASS} np-field--password ${password.className}`}>
            <div className='np-field__top flex min-h-[calc(var(--fs-xs)*1.2)] min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 [@container_(width<11.99rem)]:flex-col [@container_(width<11.99rem)]:items-start'>
              <label className={`${LABEL_CLASS} shrink-0`} htmlFor='np-pass'>
                {t('form.label.password')}
                <OkIcon />
              </label>{' '}
              <span className='np-field__hint min-w-0 flex-auto text-end text-[length:.75rem] font-medium leading-[1.2] tracking-[.01em] text-[#9AA3B5] [overflow-wrap:anywhere] [@container_(width<11.99rem)]:flex-none [@container_(width<11.99rem)]:text-start' id='np-pass-hint'>
                {t('form.password_hint', { count: PASSWORD_MIN })}
              </span>
            </div>
            <div className='np-field__control relative min-w-0'>
              <input {...register('password')} className='np-field__input' id='np-pass' type={visiblePasswords.password ? 'text' : 'password'} minLength={PASSWORD_MIN} autoComplete='new-password' placeholder={t('form.placeholder.password')} required aria-required='true' aria-invalid={password.error ? 'true' : undefined} aria-describedby='np-pass-hint np-pass-msg' />{' '}
              <button className='np-field__toggle' type='button' aria-label={visiblePasswords.password ? t('form.hide_password') : t('form.show_password')} data-visible={String(visiblePasswords.password)} aria-controls='np-pass' onClick={() => toggleVisibility('password')}>
                <EyeIcons />
              </button>
            </div>
            {renderMessage('password', 'np-pass-msg')}
          </div>

          <div className={`${FIELD_IN_ROW_CLASS} np-field--password ${passwordRepeat.className}`}>
            <label className={LABEL_CLASS} htmlFor='np-pass2'>
              {t('form.label.confirm_password')}
              <OkIcon />
            </label>
            <div className='np-field__control relative min-w-0'>
              <input {...register('passwordRepeat')} className='np-field__input' id='np-pass2' type={visiblePasswords.passwordRepeat ? 'text' : 'password'} minLength={PASSWORD_MIN} autoComplete='new-password' placeholder={t('form.placeholder.confirm_password')} required aria-required='true' aria-invalid={passwordRepeat.error ? 'true' : undefined} aria-describedby='np-pass2-msg' />{' '}
              <button className='np-field__toggle' type='button' aria-label={visiblePasswords.passwordRepeat ? t('form.hide_confirm_password') : t('form.show_confirm_password')} data-visible={String(visiblePasswords.passwordRepeat)} aria-controls='np-pass2' onClick={() => toggleVisibility('passwordRepeat')}>
                <EyeIcons />
              </button>
            </div>
            {renderMessage('passwordRepeat', 'np-pass2-msg')}
          </div>
        </div>
      </div>

      {/* The design's Turnstile slot (65px reserved) hosts the site's own captcha provider. */}
      <div className='np-form__turnstile'>
        <CaptchaProvider theme='dark' onChange={onRecaptchaChange} onExpired={onRecaptchaExpired} onError={onRecaptchaError} />
      </div>
      {errors.recaptcha && <span className='np-field__msg is-error mt-2 block'>{errors.recaptcha.message}</span>}

      <div className={`np-form__status np-form__status--top ${summary ? 'is-visible' : ''}`} data-state={summary ? 'error' : undefined}>
        <div>
          {summary && (
            <>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <circle cx={12} cy={12} r={9} />
                <path d='M12 7.8v4.9' />
                <path d='M12 16.2h.01' />
              </svg>
              <span>{summary}</span>
            </>
          )}
        </div>
      </div>

      <button className={`btn btn-primary btn--block np-form__submit mt-6 !max-w-full !gap-2 !whitespace-normal !p-3 !text-center !text-[length:.875rem] !leading-tight !tracking-[.04em] ![--_h:3.25rem] [:where(main)_&]:!py-2 [:where(main)_&]:!leading-[1.2] [@container_(width<23.99rem)]:!gap-[.375rem] [@container_(width<23.99rem)]:!px-2 [@container_(width<23.99rem)]:!text-[.8125rem] [@container_(width<23.99rem)]:!tracking-[.02em] [@container_(width>=24rem)]:!gap-3 [@container_(width>=24rem)]:!px-6 [@container_(width>=24rem)]:!tracking-[.08em] ${isLoading ? 'is-busy' : ''}`} type='submit' disabled={isLoading || isDisabledButton} aria-busy={isLoading || undefined}>
        {isLoading ? (
          <span className='np-form__submit-label'>{t('form.sending')}</span>
        ) : (
          <span className='np-form__submit-label'>
            {t('form.submit')}{' '}
            <span className='np-form__submit-end'>
              {t('form.submit_note')}
              <svg className='np-form__submit-arrow' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <path d='M5 12h14' />
                <path d='m13 6 6 6-6 6' />
              </svg>
            </span>
          </span>
        )}{' '}
        <svg className='np-form__submit-spin' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
          <path d='M12 3a9 9 0 1 1-6.4 2.6' opacity='.35' />
          <path d='M12 3a9 9 0 0 1 9 9' />
        </svg>
      </button>

      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- keeps list semantics under list-style:none (Safari) */}
      <ul className='np-form__games' role='list' aria-label={t('form.games_label')}>
        <li>Valorant</li>
        <li>CS2</li>
        <li>League of Legends</li>
        <li>Fortnite</li>
        <li>Apex Legends</li>
      </ul>

      <p className='np-form__legal max-md:!text-[length:.75rem] max-md:!leading-4'>
        <Trans t={t} i18nKey='form.legal' components={{ a: <Link href='/terms/terms-of-use' target='_blank' rel='noopener' />, span: <span className='!sr-only' />, a2: <Link href='/terms/privacy-policy' target='_blank' rel='noopener' /> }} />
      </p>
    </form>
  );
}

interface TourBoostProps extends DefaultTrialProps {}

// `daysTrial` is deliberately not read: the copy never states how long the free trial lasts.
export function TourBoost({ trialConfigId, trialPageId, isDisabledButton = false }: TourBoostProps) {
  const { t } = useTranslation([NAMESPACE, 'common']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const auth = useAuth();
  const { user } = auth;
  const router = useRouter();

  const [popUp, setPopUp] = useState<{
    show: boolean;
    message: string;
    state: AlertStateOrEmpty;
  }>({ show: false, message: '', state: '' });

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: personNameSchema(t('form.errors.required'), t('form.errors.invalid_name')),
        email: yup.string().required(t('form.errors.required')).email(t('form.errors.email')).max(255),
        repeatEmail: yup
          .string()
          .required(t('form.errors.required'))
          .email(t('form.errors.email'))
          .max(255)
          .oneOf([yup.ref('email')], t('form.errors.email_match')),
        password: yup
          .string()
          .required(t('form.errors.required'))
          .min(PASSWORD_MIN, t('form.errors.password_min', { count: PASSWORD_MIN })),
        passwordRepeat: yup
          .string()
          .required(t('form.errors.required'))
          .min(PASSWORD_MIN, t('form.errors.password_min', { count: PASSWORD_MIN }))
          .oneOf([yup.ref('password')], t('form.errors.password_match')),
        recaptcha: yup.string().required(t('form.errors.recaptcha')),
      }),
    [t]
  );

  const methods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      repeatEmail: '',
      password: '',
      passwordRepeat: '',
      recaptcha: '',
    },
    mode: 'onBlur',
  });
  const { setValue, setError, clearErrors } = methods;

  async function handleLogin({ email, password, recaptcha }: { email: string; password: string; recaptcha: string }) {
    // Awaited, not raced: a logout landing after this login would end the new session. Its result does not
    // gate the login, which replaces whoever the server session held.
    if (user) await auth.logout();
    // eslint-disable-next-line func-names
    auth.login({ login: email, password, recaptcha }, function () {});
  }

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required' });
  }, [setValue, setError]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    if (typeof window !== 'undefined' && (window as any).captchaReset) {
      (window as any).captchaReset();
    }
  }, [setValue]);

  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const handleSsoStart = useCallback(() => {
    setIsSsoAuthenticating(true);
  }, []);
  const handleSsoSuccess = useCallback(() => {
    setIsSsoAuthenticating(false);
  }, []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const utm = getUtmData();

    const body: CreateUserTrialParams = {
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha: data.recaptcha,
      telegram: null,
      password_repeat: data.passwordRepeat,
      ref: '',
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      custom: trialConfigId,
      trialPageId,
      ...utm,
    };

    auth.createTrial(
      {
        ...body,
      },
      (error) => {
        // Reset captcha on error
        setValue('recaptcha', '');
        if (typeof window !== 'undefined' && (window as any).captchaReset) {
          (window as any).captchaReset();
        }
        setPopUp({
          show: true,
          message: t(apiErrorMessageKey(error, { fallbackKey: 'form.errors.default_create_account', statusKeys: TRIAL_STATUS_KEYS })),
          state: 'error',
        });
        setIsLoading((prevState) => !prevState);
      },
      () => {
        handleLogin({ email: data.email, password: data.password, recaptcha: data.recaptcha });
        sendGTMEvent({ event: 'createAccount' });
        sessionStorage.setItem('customTrialApproved', 'true');
        router.push('/thanks-for-trial-no-card');
      },
      /* showSuccessToast */ false
    );
  };

  // The card moves between the hero column and the dialog, so the captcha widget remounts with it:
  // its token belongs to the widget that is gone.
  const moveForm = useCallback(
    (open: boolean) => {
      setValue('recaptcha', '');
      setIsDialogOpen(open);
    },
    [setValue]
  );
  const openTrial = useCallback(() => moveForm(true), [moveForm]);
  const closeTrial = useCallback(() => moveForm(false), [moveForm]);

  const form = user ? (
    <div className='np-form relative isolate mx-auto w-full max-w-full rounded-3xl !border !border-white/10 p-6 text-[#F4F6F7]' id='np-form'>
      <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
    </div>
  ) : (
    <TrialForm methods={methods} onSubmit={onSubmit} isLoading={isLoading} isDisabledButton={isDisabledButton} trialConfigId={trialConfigId} trialPageId={trialPageId} popUp={popUp} onClosePopUp={() => setPopUp((previous) => ({ ...previous, show: false }))} isSsoAuthenticating={isSsoAuthenticating} onSsoStart={handleSsoStart} onSsoSuccess={handleSsoSuccess} onSsoError={handleSsoError} onRecaptchaChange={handleRecaptchaChange} onRecaptchaExpired={handleRecaptchaExpired} onRecaptchaError={handleRecaptchaError} />
  );

  return <TourBoostPage form={form} isDialogOpen={isDialogOpen} onOpenTrial={openTrial} onCloseTrial={closeTrial} />;
}
